import { useMemo, useState } from 'react'

export interface LocationRecord {
  galpao: string
  rua: string
  posicao_pallete: string
  escaninho_nivel: string
}

export interface LocationOption {
  id: number
  description: string
}

const toOptions = (values: string[]): LocationOption[] => Array.from(new Set(values))
  .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
  .map((description, id) => ({ id, description }))

export interface LocationCascade {
  shedOptions: LocationOption[]
  streetOptions: LocationOption[]
  shelfOptions: LocationOption[]
  binOptions: LocationOption[]
  shedId: number | ''
  streetId: number | ''
  shelfId: number | ''
  binId: number | ''
  shed: string
  street: string
  shelf: string
  bin: string
  onShedChange: (id: number | '') => void
  onStreetChange: (id: number | '') => void
  onShelfChange: (id: number | '') => void
  onBinChange: (id: number | '') => void
  touched: boolean
  markTouched: () => void
  isComplete: boolean
  reset: () => void
}

export const useLocationCascade = (locations: LocationRecord[]): LocationCascade => {
  const [shedId, setShedId] = useState<number | ''>('')
  const [streetId, setStreetId] = useState<number | ''>('')
  const [shelfId, setShelfId] = useState<number | ''>('')
  const [binId, setBinId] = useState<number | ''>('')
  const [touched, setTouched] = useState(false)

  const shedOptions = useMemo(
    () => toOptions(locations.map((location) => location.galpao)),
    [locations],
  )
  const shed = shedOptions.find((option) => option.id === shedId)?.description ?? ''

  const streetOptions = useMemo(
    () => toOptions(
      locations.filter((location) => location.galpao === shed).map((location) => location.rua),
    ),
    [locations, shed],
  )
  const street = streetOptions.find((option) => option.id === streetId)?.description ?? ''

  const shelfOptions = useMemo(
    () => toOptions(
      locations
        .filter((location) => location.galpao === shed && location.rua === street)
        .map((location) => location.posicao_pallete),
    ),
    [locations, shed, street],
  )
  const shelf = shelfOptions.find((option) => option.id === shelfId)?.description ?? ''

  const binOptions = useMemo(
    () => toOptions(
      locations
        .filter((location) => (
          location.galpao === shed
          && location.rua === street
          && location.posicao_pallete === shelf
        ))
        .map((location) => location.escaninho_nivel),
    ),
    [locations, shed, street, shelf],
  )
  const bin = binOptions.find((option) => option.id === binId)?.description ?? ''

  const onShedChange = (id: number | '') => {
    setShedId(id)
    setStreetId('')
    setShelfId('')
    setBinId('')
  }
  const onStreetChange = (id: number | '') => {
    setStreetId(id)
    setShelfId('')
    setBinId('')
  }
  const onShelfChange = (id: number | '') => {
    setShelfId(id)
    setBinId('')
  }
  const onBinChange = (id: number | '') => setBinId(id)

  const reset = () => {
    setShedId('')
    setStreetId('')
    setShelfId('')
    setBinId('')
    setTouched(false)
  }

  return {
    shedOptions,
    streetOptions,
    shelfOptions,
    binOptions,
    shedId,
    streetId,
    shelfId,
    binId,
    shed,
    street,
    shelf,
    bin,
    onShedChange,
    onStreetChange,
    onShelfChange,
    onBinChange,
    touched,
    markTouched: () => setTouched(true),
    isComplete: Boolean(shed && street && shelf && bin),
    reset,
  }
}
